import { Divider, Grid, Typography } from '@material-ui/core';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmeraldError } from 'src/common/api/emerald-error';
import BaseButton from 'src/common/components/base-button';
import { ITenderAdd } from 'src/common/interfaces/dtos/IPreTenderAdd';
import { AppState, store } from 'src/common/redux/configure-store';
import { DeviceResponse } from 'src/devices/rds-connection';
import { getCheckDetails } from 'src/devices/micr-operations';
import { getMICRData, parseCheckData, parseTenderDetails } from 'src/devices/micr-utilities';
import { makePaymentAndSetDefaultTender } from 'src/tendering/business-logic/handle-tender-payment';
import { styles } from 'src/tendering/components/process-tender/tender-processing-view-styles';
import TenderAmountDue from 'src/tendering/components/tender-amount-due';
import TenderGroupTitle from 'src/tendering/components/tender-group-title';
import { styles as micrStyles } from './micr-processing-view-styles';
import { AbortedError } from 'src/common/api/extended-emerald-error';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { ReactComponent as MicrIcon } from 'src/common/media/micr.svg';
import { themeColor } from 'src/mui-theme';
import {
  PageStatus,
  restorePage,
  setHomePage,
} from 'src/common/components/emerald-home-page/emerald-home-page';
import { showManualCheckValidation } from './manual-check-validation';
import { showMICRErrorModal } from './micr-modal';
import { TenderingModeHomePage } from 'src/home-page/components/home-page-tendering';
import {
  CenterMessagingValidationProps,
  MicrProcessingViewProps,
  ScreenState,
  Translations,
} from './check-processing-types';
import { MICRActions } from 'src/common/redux/reducers/micr-reducer';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { useSelector } from 'react-redux';
import TrainingModeWatermark from 'src/common/training-mode/training-mode-watermark';
import XLRLogger from 'src/common/helpers/logger';

export const MicrProcessingPage = 'MicrProcessingPage';

export const showMicrProcessingView = (lineTender: ITenderAdd, err: EmeraldError): void => {
  setHomePage(
    <MicrProcessingView
      amountDue={store.getState().itemList.amountDue}
      tenderDetails={lineTender}
      missingParamsErr={err}
    />,
    undefined,
    { pageId: MicrProcessingPage } as PageStatus,
  );
};

const closeMicrProcessingView = () => restorePage(TenderingModeHomePage);

const MicrProcessingView: React.FC<MicrProcessingViewProps> = (
  props: MicrProcessingViewProps,
): JSX.Element => {
  const classes = styles();
  const micrClasses = micrStyles();
  const trainingModeClasses = trainingModeStyles();
  const trainingMode = useSelector((state: AppState): boolean => state.posMode.trainingMode);
  const { t } = useTranslation();
  const { amountDue } = props;
  const [displayText, setDisplayText] = useState<string>(t('MICRInsertCheck'));
  const [processing, setProcessing] = useState<boolean>(false);
  const translations: Translations = {
    processingIndication: t('ProcessingIndication'),
  };

  const screenState: ScreenState = {
    setDisplayText,
    setProcessing,
    displayText,
    processing,
  };

  return (
    <Grid
      item
      container
      direction='row'
      justifyContent='space-between'
      className={`fullHeight flexFull ${classes.viewContainer} ${
        trainingMode ? trainingModeClasses.trainingModeBackground : ''
      }`}
    >
      <Grid
        item
        container
        direction='row'
        justifyContent='space-between'
        className={classes.viewHeader}
      >
        <Grid item lg={9}>
          <TenderGroupTitle title={store.getState().tenderMenu.selectedTenderGroup?.name || ''} />
        </Grid>
        <Grid item>
          <TenderAmountDue amountDue={amountDue} align='right' />
        </Grid>
      </Grid>

      <Grid item container direction='row' className={micrClasses.pageContent}>
        <CenterMessagingColumn
          pageData={props}
          screenState={screenState}
          translations={translations}
        />
      </Grid>

      {trainingMode && (
        <TrainingModeWatermark
          dataTestId='action-window-training-mode-watermark'
          supplementalStyles={trainingModeClasses.trainingWaterMark}
        />
      )}
    </Grid>
  );
};

export default MicrProcessingView;

const CenterMessagingColumn: React.FC<CenterMessagingValidationProps> = ({
  pageData,
  screenState,
  translations,
}) => {
  const classes = styles();
  const micrClasses = micrStyles();
  const { t } = useTranslation();
  const { displayText, processing } = screenState;
  const failedAttempts = useRef(0);

  return (
    <Grid container md={7} direction='column' className={micrClasses.centerPageContent}>
      <Grid
        item
        container
        direction='column'
        data-testid='payment-info'
        className={micrClasses.checkScreenMessageGridContainer}
      >
        <Grid item data-testid='payment-title' className={micrClasses.checkScreenMessageTitleGrid}>
          <Typography variant='h2'>{t('MicrValidationTitle')}</Typography>
        </Grid>
        <Grid item className={micrClasses.checkScreenDividerGrid}>
          <div data-testid='payment-title-bar' className={`${micrClasses.paymentTitleBar}`}></div>
        </Grid>
        <Grid
          item
          data-testid='validation-message'
          className={micrClasses.checkScreenMessageBodyGrid}
        >
          <Typography variant='body1'>{t(displayText ?? '')}</Typography>
        </Grid>
        {!processing && (
          <>
            <Grid item>
              <MicrIcon
                width={'2.342606149341142vw'}
                height={'4.166666666666667vh'}
                fill={themeColor.secondaryMain}
              />
            </Grid>
            <Grid item container direction='row' className={micrClasses.dividerContainer}>
              <Grid item className={micrClasses.divider}>
                <Divider />
              </Grid>
              <Grid item className={micrClasses.dividerMiddle}>
                <Typography variant='body1'>{t('or')}</Typography>
              </Grid>
              <Grid item className={micrClasses.divider}>
                <Divider />
              </Grid>
            </Grid>
            <Grid item>
              <Typography variant='body1'>{t('MicrValidateManually')}</Typography>
            </Grid>
          </>
        )}
      </Grid>
      <Grid container className={micrClasses.checkScreenBottomButtons}>
        <Grid item container direction='row' className={classes.exitButtonContainer}>
          <BaseButton
            color='inherit'
            buttonType='white'
            className={`${micrClasses.containedButton} ${micrClasses.bottomButton}`}
            onClick={() => closeMicrProcessingView()}
            data-testid='check-processing-cancel'
          >
            <Typography variant='button'>{t('Cancel')}</Typography>
          </BaseButton>
          <BaseButton
            buttonType='white'
            className={`${micrClasses.containedButton} ${micrClasses.bottomButton}`}
            onClick={() => goToManualEntry(pageData)}
            data-testid='check-processing-manual'
          >
            <Typography variant='button'>{t('MicrManualButton')}</Typography>
          </BaseButton>
          <BaseButton
            color='secondary'
            className={`${micrClasses.bottomButton}`}
            onClick={() => process(pageData, screenState, translations, failedAttempts)}
            data-testid='check-validation-button'
          >
            <Typography variant='button'>{t('MicrValidationButton')}</Typography>
          </BaseButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

const goToManualEntry = (pageData: MicrProcessingViewProps) => {
  const { missingParamsErr, amountDue, tenderDetails } = pageData;
  const deviceResponse: DeviceResponse = {
    success: false,
    metaDataMap: { key: '', string: '' },
  };
  const checkParameters = parseCheckData(deviceResponse);
  if (missingParamsErr) {
    missingParamsErr.data = {
      ...missingParamsErr?.data,
      requiredFields: checkParameters,
      message: 'Personal Check is missing required parameters',
    };
  }
  showManualCheckValidation({
    defaultValue: amountDue,
    tenderAdd: tenderDetails,
    chequeData: missingParamsErr,
  });
};

const showTheMICRModal = (
  { tenderDetails, missingParamsErr }: MicrProcessingViewProps,
  { setDisplayText }: ScreenState,
  { micrInsertCheck }: Translations,
  failedAttempts: React.MutableRefObject<number>,
): void => {
  failedAttempts.current++;
  if (failedAttempts.current >= 3) {
    showManualCheckValidation({
      defaultValue: tenderDetails.amount?.amount,
      tenderAdd: tenderDetails,
      chequeData: missingParamsErr,
    });
  } else {
    setDisplayText(micrInsertCheck);
    showMICRErrorModal({
      onRetry: () => {},
      onClose: closeMicrProcessingView,
    });
  }
};

const process = async (
  screenData: MicrProcessingViewProps,
  screenState: ScreenState,
  translations: Translations,
  failedAttempts: React.MutableRefObject<number>,
) => {
  const { tenderDetails } = screenData;
  const { setDisplayText, setProcessing } = screenState;
  const { processingIndication } = translations;
  withSpinner(async () => {
    setProcessing(true);
    setDisplayText(processingIndication);
    await processMICRData(tenderDetails);
  })
    .catch(async err => {
      XLRLogger.error({
        message: 'Error in CheckValidation',
        data: err,
        source: 'micr-validation-view',
      });
      showTheMICRModal(screenData, screenState, translations, failedAttempts);
    })
    .finally(() => setProcessing(false));
};

export const processMICRData = async (tenderDetails: ITenderAdd): Promise<void> => {
  const checkDetails = getMICRData() ?? (await getCheckDetails());
  const compiledTenderDetails = parseTenderDetails(tenderDetails, checkDetails);
  await makePaymentAndSetDefaultTender(compiledTenderDetails)
    .catch(err => {
      if (!(err instanceof AbortedError && err.code === 'ProcessCheck')) {
        throw err;
      }
    })
    .finally(() => store.dispatch(MICRActions.resetMICRData()));
};
